import type { AuthUser, LoginPayload } from '../types/auth.types'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000/api/v1'
const TENANT_SLUG = import.meta.env.VITE_TENANT_SLUG || 'studio-luebeck'
const BASE = `${API_BASE_URL}/tenants/${TENANT_SLUG}/auth`

/**
 * Fehler eines Anmelde-Aufrufs. `status` 0 = Server nicht erreichbar (offline, Funkloch) - das ist etwas anderes
 * als "Token ungültig" (401/403): nur Letzteres darf eine Sitzung beenden.
 */
export class AuthRequestError extends Error {
  constructor(message: string, readonly status: number) {
    super(message)
  }
}

async function request<T>(path: string, options?: RequestInit, errorLabel = 'Anmelde-Fehler'): Promise<T> {
  let response: Response
  try {
    response = await fetch(`${BASE}${path}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', ...(options?.headers || {}) }
    })
  } catch {
    throw new AuthRequestError('Keine Verbindung zum Server', 0)
  }
  if (!response.ok) {
    let detail = ''
    try {
      const body = await response.json()
      detail = body?.error ? `: ${body.error}` : ''
    } catch {
      // kein JSON-Body
    }
    throw new AuthRequestError(`${errorLabel}${detail}`, response.status)
  }
  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

/**
 * Öffentliche Aufrufe rund um „Passwort vergessen“ (ohne Anmeldung). Anders
 * als request() liefert der Fehler genau den Text des Backends, weil die
 * Seiten ihn direkt anzeigen.
 */
async function publicRequest<T>(url: string, body: unknown): Promise<T> {
  let response: Response
  try {
    response = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
  } catch {
    throw new Error('Keine Verbindung. Bitte später noch einmal versuchen.')
  }
  const data = await response.json().catch(() => ({}))
  if (!response.ok) throw new Error(data?.error || 'Das hat nicht geklappt. Bitte später noch einmal versuchen.')
  return data as T
}

export const authService = {
  async login(payload: LoginPayload): Promise<{ user: AuthUser; token: string }> {
    return request('/login', { method: 'POST', body: JSON.stringify(payload) })
  },

  async me(token: string): Promise<AuthUser> {
    const { user } = await request<{ user: AuthUser }>('/me', {
      headers: { Authorization: `Bearer ${token}` }
    })
    return user
  },

  async logout(token: string): Promise<void> {
    await request('/logout', { method: 'POST', headers: { Authorization: `Bearer ${token}` } })
  },

  /** Liefert ein frisches Token - das alte ist danach ungültig (alle anderen Geräte abgemeldet). */
  async changePassword(token: string, currentPassword: string, newPassword: string): Promise<string> {
    const { token: newToken } = await request<{ token: string }>(
      '/change-password',
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify({ currentPassword, newPassword })
      },
      'Passwort konnte nicht geändert werden'
    )
    return newToken
  },

  /**
   * Link zum Zurücksetzen anfordern. Antwortet immer gleich, auch ohne Konto
   * (siehe backend/src/utils/password-reset.ts). Mit app=siebenklang enthält
   * die Mail einen Rückweg zu Siebenklang.
   */
  async forgotPassword(email: string, app: 'studiopro' | 'siebenklang' = 'studiopro'): Promise<void> {
    const url = app === 'siebenklang' ? `${API_BASE_URL}/tenants/${TENANT_SLUG}/siebenklang/forgot-password` : `${BASE}/forgot-password`
    await publicRequest(url, { email })
  },

  async checkResetToken(token: string): Promise<boolean> {
    const { valid } = await publicRequest<{ valid: boolean }>(`${BASE}/reset-password/check`, { token })
    return valid
  },

  async resetPassword(token: string, newPassword: string): Promise<void> {
    await publicRequest(`${BASE}/reset-password`, { token, newPassword })
  }
}
